import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Boxes, Copy, ExternalLink, Pause, Play, Plus, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { RoomSummary } from "@/api/generated/RoomSummary"
import {
  ROOM_PHASES,
  type RoomPhase,
  useDeleteRoom,
  useDuplicateRoom,
  useRooms,
  useRoomTeamPicker,
  useToggleRoomPause,
} from "@/api/queries/workplace"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState } from "@/components/app/states"
import { Button } from "@/components/ui/button"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { NewRoomDialog } from "@/features/workplace/new-room-dialog"
import { PhaseBadge } from "@/features/workplace/room-status"
import { formatNumber } from "@/lib/format"

interface RoomsSearch {
  q?: string
  team?: number
  phase?: RoomPhase
  new?: boolean
}

export const Route = createFileRoute("/_app/workplace/")({
  staticData: { titleKey: "nav.items.rooms" },
  validateSearch: (s: Record<string, unknown>): RoomsSearch => {
    const team = Number(s.team)
    return {
      q: typeof s.q === "string" && s.q ? s.q : undefined,
      team: Number.isFinite(team) && team > 0 ? team : undefined,
      phase: (ROOM_PHASES as readonly unknown[]).includes(s.phase) ? (s.phase as RoomPhase) : undefined,
      new: s.new === true || s.new === "true" || s.new === 1 ? true : undefined,
    }
  },
  component: RoomsPage,
})

function RoomsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setSearch = (patch: Partial<RoomsSearch>) =>
    void navigate({ search: (s) => ({ ...s, ...patch }), replace: true })
  const [q, setQ] = useState(search.q ?? "")
  useEffect(() => {
    const id = setTimeout(() => {
      if ((search.q ?? "") !== q)
        void navigate({ search: (s) => ({ ...s, q: q || undefined }), replace: true })
    }, 300)
    return () => clearTimeout(id)
  }, [q, search.q, navigate])

  const rooms = useRooms({ q: search.q, team_id: search.team, run_phase: search.phase })
  const teams = useRoomTeamPicker()
  const remove = useDeleteRoom()
  const duplicate = useDuplicateRoom()
  const toggle = useToggleRoomPause()
  const confirm = useConfirm()
  const open = (r: RoomSummary) =>
    void navigate({ to: "/workplace/$roomId", params: { roomId: String(r.id) } })

  const columns = useMemo(
    () =>
      createColumns<RoomSummary>((c) => [
        c.accessor((r) => r.title, {
          id: "title",
          header: t("workplace.rooms.columns.room"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-48">
              <div className="truncate font-medium">
                {row.original.title || t("workplace.rooms.untitled", { id: row.original.id })}
              </div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.team_name ?? t("workplace.rooms.noTeam")}
              </div>
            </div>
          ),
        }),
        c.accessor("run_phase", {
          header: t("workplace.rooms.columns.status"),
          meta: { label: t("workplace.rooms.columns.status") },
          cell: ({ getValue }) => <PhaseBadge phase={getValue()} />,
        }),
        c.accessor("turn_count", {
          header: t("workplace.rooms.columns.turns"),
          meta: { label: t("workplace.rooms.columns.turns"), align: "end" },
          cell: ({ row }) => (
            <span className="tabular-nums">
              {formatNumber(row.original.turn_count)}
              {row.original.max_turns_budget != null && (
                <span className="text-muted-foreground">
                  {" "}
                  / {formatNumber(row.original.max_turns_budget)}
                </span>
              )}
            </span>
          ),
        }),
        c.accessor("run_phase_updated_at", {
          header: t("workplace.rooms.columns.activity"),
          meta: { label: t("workplace.rooms.columns.activity") },
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
        c.accessor("created_at", {
          header: t("workplace.rooms.columns.created"),
          meta: { label: t("workplace.rooms.columns.created") },
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
      ]),
    [t],
  )

  const teamItems = [
    { value: "all", label: t("workplace.rooms.allTeams") },
    ...(teams.data ?? []).map((x) => ({ value: String(x.id), label: x.name })),
  ]
  const phaseItems = [
    { value: "all", label: t("workplace.rooms.allStatuses") },
    ...ROOM_PHASES.map((p) => ({ value: p, label: t(`workplace.phase.${p}`) })),
  ]
  const filtered = !!(search.q || search.team || search.phase)

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("workplace.rooms.title")}
        description={t("workplace.rooms.description")}
        icon={Boxes}
        actions={
          <Button onClick={() => setSearch({ new: true })} data-testid="new-room">
            <Plus />
            {t("workplace.rooms.new")}
          </Button>
        }
      />
      <DataTable
        label={t("workplace.rooms.title")}
        data={rooms.data?.items}
        columns={columns}
        getRowId={(r) => String(r.id)}
        loading={rooms.isPending}
        error={rooms.error}
        onRetry={() => void rooms.refetch()}
        searchable
        search={q}
        onSearchChange={setQ}
        searchPlaceholder={t("workplace.rooms.search")}
        persistKey="workplace-rooms"
        columnToggle
        initialColumnVisibility={{ created_at: false }}
        initialSorting={[{ id: "run_phase_updated_at", desc: true }]}
        toolbar={
          <>
            <Select
              items={teamItems}
              value={search.team ? String(search.team) : "all"}
              onValueChange={(v) => setSearch({ team: v && v !== "all" ? Number(v) : undefined })}
            >
              <SelectTrigger size="sm" className="min-w-36" aria-label={t("workplace.rooms.filterTeam")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {teamItems.map((i) => (
                  <SelectItem key={i.value} value={i.value}>
                    {i.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              items={phaseItems}
              value={search.phase ?? "all"}
              onValueChange={(v) => setSearch({ phase: v && v !== "all" ? (v as RoomPhase) : undefined })}
            >
              <SelectTrigger size="sm" className="min-w-36" aria-label={t("workplace.rooms.filterStatus")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {phaseItems.map((i) => (
                  <SelectItem key={i.value} value={i.value}>
                    {i.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
        onRowClick={open}
        rowActions={(r) => (
          <>
            <DropdownMenuItem onClick={() => open(r)}>
              <ExternalLink />
              {t("workplace.rooms.open")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => toggle.mutate(r)}>
              {r.paused ? <Play /> : <Pause />}
              {r.paused ? t("workplace.room.control.resume") : t("workplace.room.control.pause")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => duplicate.mutate({ id: r.id })}>
              <Copy />
              {t("workplace.rooms.duplicate")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                void confirm({
                  title: t("workplace.rooms.deleteTitle"),
                  description: t("workplace.rooms.deleteDescription", {
                    title: r.title || t("workplace.rooms.untitled", { id: r.id }),
                  }),
                  confirmLabel: t("workplace.rooms.delete"),
                  destructive: true,
                  onConfirm: () => remove.mutateAsync(r),
                })
              }
            >
              <Trash2 />
              {t("workplace.rooms.delete")}
            </DropdownMenuItem>
          </>
        )}
        emptyState={
          filtered ? undefined : (
            <EmptyState
              variant="plain"
              icon={Boxes}
              title={t("workplace.rooms.emptyTitle")}
              description={t("workplace.rooms.emptyDescription")}
              action={
                <Button size="sm" onClick={() => setSearch({ new: true })}>
                  <Plus />
                  {t("workplace.rooms.new")}
                </Button>
              }
            />
          )
        }
      />
      <NewRoomDialog
        open={!!search.new}
        onOpenChange={(o) => setSearch({ new: o ? true : undefined })}
        defaultTeamId={search.team}
      />
    </PageContainer>
  )
}
